import { motion } from "framer-motion";
import { ArrowRight, Check, AlertCircle } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import { PHOTOS, TIDYCAL } from "@/lib/images";

export default function Sessions() {
  const { t } = useT();
  const s = t.sessions;

  // Map service key → TidyCal URL
  const bookingFor = (key) => TIDYCAL[key];

  return (
    <section
      id="sesiones"
      data-testid="sessions-section"
      className="relative py-24 md:py-36 bg-[#4B2F4F] text-[#F7F3F7] vk-grain overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Section opener */}
        <div className="grid grid-cols-12 gap-6 md:gap-12 items-end mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.24em] text-[#F7F3F7]/60 mb-6">
              {s.eyebrow}
            </p>
            <h2
              data-testid="sessions-headline"
              className="font-serif font-light text-[clamp(2.2rem,5vw,4.5rem)] leading-[0.95] tracking-tight"
            >
              {s.headline.l1}
              <br />
              <span className="italic">{s.headline.l2}</span>
              <br />
              {s.headline.l3}
            </h2>
            <p className="mt-8 text-lg leading-relaxed text-[#F7F3F7]/80 max-w-xl">
              {s.subtitle}
            </p>
          </div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="col-span-12 md:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={PHOTOS.sessionsImage}
                alt="Trabajando desde casa con video llamada, libreta y un café."
                className="w-full h-full object-cover"
                data-testid="sessions-image"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-[#4B2F4F]/10 mix-blend-multiply" />
            </div>
            <div className="hidden md:block absolute -top-5 -right-5 w-24 h-24 border border-[#E0B25B]/40" />
          </motion.div>
        </div>

        {/* Service detail cards — full rich content */}
        <div className="border-t border-[#F7F3F7]/15 pt-16 md:pt-20">
          <div className="grid grid-cols-12 gap-6 mb-12 md:mb-16">
            <div className="col-span-12 md:col-span-8">
              <p className="text-[11px] uppercase tracking-[0.24em] text-[#F7F3F7]/60 mb-6">
                {s.othersTitle}
              </p>
              <h3 className="font-serif font-light text-3xl md:text-5xl leading-tight tracking-tight">
                {s.othersLead}
              </h3>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#F7F3F7]/10">
            {s.services.map((svc, i) => (
              <motion.article
                key={svc.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                data-testid={`service-card-${svc.key}`}
                className="relative bg-[#4B2F4F] p-8 md:p-10 flex flex-col"
              >
                <header>
                  <p className="font-serif text-5xl text-[#E0B25B] leading-none">
                    0{i + 1}
                  </p>
                  <div className="my-6 w-12 h-px bg-[#F7F3F7]/20" />
                  <h4
                    data-testid={`service-name-${svc.key}`}
                    className="font-serif text-[26px] md:text-[28px] leading-[1.1] tracking-tight"
                  >
                    {svc.name}
                  </h4>
                  <p
                    data-testid={`service-duration-${svc.key}`}
                    className="mt-2 text-[11px] uppercase tracking-[0.22em] text-[#F7F3F7]/60"
                  >
                    {svc.duration}
                  </p>
                </header>

                <p className="mt-6 text-[15px] leading-relaxed text-[#F7F3F7]/90 italic">
                  {svc.forYou}
                </p>

                <p className="mt-5 text-[15px] leading-relaxed text-[#F7F3F7]/80">
                  {svc.description}
                </p>

                <div className="mt-7">
                  <p className="text-[10px] uppercase tracking-[0.24em] text-[#E0B25B] mb-3">
                    {s.includesLabel}
                  </p>
                  <ul className="space-y-2.5">
                    {svc.includes.map((it, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-3 text-[14px] leading-relaxed text-[#F7F3F7]/85"
                      >
                        <Check
                          size={14}
                          strokeWidth={1.4}
                          className="mt-1 flex-shrink-0 text-[#E0B25B]"
                        />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-7 pt-6 border-t border-[#F7F3F7]/15">
                  <p className="text-[10px] uppercase tracking-[0.24em] text-[#F7F3F7]/55 mb-2">
                    {s.idealLabel}
                  </p>
                  <p className="text-[14px] leading-relaxed text-[#F7F3F7]/80">
                    {svc.idealFor}
                  </p>
                </div>

                <div className="mt-auto pt-8 flex items-end justify-between gap-4 border-t border-[#F7F3F7]/15 mt-8">
                  <span
                    data-testid={`service-price-${svc.key}`}
                    className="font-serif text-3xl text-[#F7F3F7]"
                  >
                    {svc.price}
                  </span>
                  <a
                    href={bookingFor(svc.key)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`service-cta-${svc.key}`}
                    className="group inline-flex items-center gap-2 bg-[#E0B25B] text-[#4B2F4F] px-5 py-3 text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#F7F3F7] transition-colors duration-300"
                  >
                    {s.reserve}
                    <ArrowRight
                      size={14}
                      strokeWidth={1.6}
                      className="group-hover:translate-x-0.5 transition-transform"
                    />
                  </a>
                </div>
              </motion.article>
            ))}
          </div>

          {/* Important note */}
          <div
            data-testid="sessions-important-note"
            className="mt-14 max-w-3xl border-l-2 border-[#E0B25B] pl-6 md:pl-8"
          >
            <div className="flex items-center gap-3 mb-3">
              <AlertCircle
                size={18}
                strokeWidth={1.4}
                className="text-[#E0B25B]"
              />
              <p className="text-[11px] uppercase tracking-[0.26em] text-[#E0B25B]">
                {s.importantTitle}
              </p>
            </div>
            <p className="text-base leading-relaxed text-[#F7F3F7]/75">
              {s.importantNote}
            </p>
            <p className="mt-4 text-[12px] tracking-[0.04em] text-[#F7F3F7]/55">
              {t.footer.languages}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
